(() => {
  'use strict';
  const authKey = 'jacobs-community-auth-v1';
  const overlaySelector = '.jl-drawer.is-open,.jl-search-overlay.is-open,.jl-lightbox.is-open,.dialog-panel:not([hidden])';
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = String(text);
    return element;
  };
  const action = (text, name, quiet = false) => {
    const button = node('button', quiet ? 'jl-chat-button jl-chat-button--quiet' : 'jl-chat-button', text);
    button.type = 'button'; button.dataset.chatAction = name; return button;
  };
  class CommunityChat extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true; this.owner = this.dataset.customer || ''; this.sequence = 0; this.searchSequence = 0;
      this.signingOut = false; this.requests = new Set(); this.drafts = new Map(); this.pending = new Map(); this.messages = new Map();
      this.words = JSON.parse(this.querySelector('[data-chat-strings]').textContent);
      this.panel = this.querySelector('dialog'); this.launcher = this.querySelector('[data-chat-launch]');
      this.view = this.querySelector('[data-chat-view]'); this.status = this.querySelector('[data-chat-status]');
      this.listeners = new AbortController(); const options = {signal:this.listeners.signal};
      this.launcher.addEventListener('click', () => this.open(), options);
      this.querySelector('[data-chat-close]').addEventListener('click', () => this.close(), options);
      this.addEventListener('click', event => this.click(event), options);
      this.addEventListener('submit', event => this.submit(event), options);
      this.addEventListener('input', event => {
        if (event.target.name === 'text' && this.target && !this.pending.has(this.targetKey())) this.drafts.set(this.targetKey(), event.target.value);
      }, options);
      this.panel.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.panel.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); this.close(); } }, options);
      document.addEventListener('jl:community-signing-out', () => this.resetIdentity(), options);
      document.addEventListener('jl:community-session-changed', () => { if (this.panel.open && !this.signingOut) this.bootstrap(); }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.invalidate();
        else if (this.panel.open && !this.signingOut) this.bootstrap();
      }, options);
      document.addEventListener('focusin', () => this.syncOverlays(), options);
      document.addEventListener('focusout', () => queueMicrotask(() => this.syncOverlays()), options);
      window.addEventListener('storage', event => { if (event.key === authKey && !this.identityCurrent()) this.resetIdentity(); }, options);
      window.addEventListener('pagehide', () => this.close(false), options);
      window.addEventListener('resize', () => this.position(), options);
      window.visualViewport?.addEventListener('resize', () => this.position(), options);
      window.visualViewport?.addEventListener('scroll', () => this.position(), options);
      this.observer = new MutationObserver(() => this.syncOverlays());
      this.observer.observe(document.body, {subtree:true,attributes:true,attributeFilter:['class','hidden']});
      this.launcher.hidden = false; this.position(); this.syncOverlays();
    }
    disconnectedCallback() { this.invalidate(); this.listeners?.abort(); this.observer?.disconnect(); this.clearPrivate(); this.ready = false; }
    word(key) { return this.words[key] || key; }
    identityCurrent() {
      if (this.signingOut) return false;
      try { const raw = localStorage.getItem(authKey); if (!raw) return true; const record = JSON.parse(raw); return record?.stage === 'active' && record.customer === this.owner; }
      catch { return false; }
    }
    current(sequence) { return sequence === this.sequence && this.panel.open && !document.hidden && this.identityCurrent(); }
    invalidate() {
      this.sequence++; this.searchSequence++; clearTimeout(this.timer); this.timer = null;
      for (const controller of this.requests) controller.abort();
      this.requests.clear(); this.polling = false;
    }
    clearPrivate() {
      this.profile = null; this.csrf = null; this.target = null; this.messages.clear(); this.drafts.clear(); this.pending.clear();
      this.view.replaceChildren(); this.log = null; this.form = null; this.content = null; this.safety = null;
    }
    resetIdentity() { this.signingOut = true; this.invalidate(); this.clearPrivate(); this.say(this.word('signedout')); this.guest(); }
    say(text = '', retry) {
      this.status.replaceChildren(node('span', '', text));
      if (retry) { const button = action(this.word('retry'),'retry',true); button.addEventListener('click',retry,{once:true}); this.status.append(button); }
    }
    syncOverlays() {
      const blocked = Boolean(document.querySelector(overlaySelector));
      if (blocked && this.panel.open) this.close(false);
      const active = document.activeElement;
      const typing = window.matchMedia('(max-width:600px)').matches && active?.matches?.('input,textarea,[contenteditable="true"]') && !active.closest('jl-community-chat');
      const hidden = blocked || this.panel.open || Boolean(typing);
      if (this.launcher.hidden !== hidden) this.launcher.hidden = hidden;
    }
    position() {
      const bar = document.querySelector('[data-kenya-mobile-give]'); const rect = bar?.getBoundingClientRect();
      const height = rect && getComputedStyle(bar).display !== 'none' && rect.bottom > 0 && rect.top < window.innerHeight ? rect.height : 0;
      this.style.setProperty('--chat-bottom', `${20 + height}px`);
      this.style.setProperty('--chat-viewport-height', `${window.visualViewport?.height || window.innerHeight}px`);
      this.style.setProperty('--chat-viewport-top', `${window.visualViewport?.offsetTop || 0}px`);
      const mobile = window.matchMedia('(max-width:600px)').matches;
      this.dataset.compact = String(mobile && (window.visualViewport?.height || window.innerHeight) < 560);
      if (this.panel?.open && this.panel.getAttribute('aria-modal') !== String(mobile)) {
        const focused = document.activeElement;
        this.panel.close();
        if (mobile) this.panel.showModal(); else this.panel.show();
        this.panel.setAttribute('aria-modal',String(mobile));
        if (focused && this.panel.contains(focused)) focused.focus({preventScroll:true});
      }
    }
    open() {
      if (document.querySelector(overlaySelector) || this.panel.open) return;
      this.position();
      if (window.matchMedia('(max-width:600px)').matches) this.panel.showModal(); else this.panel.show();
      this.panel.setAttribute('aria-modal',String(window.matchMedia('(max-width:600px)').matches));
      this.launcher.setAttribute('aria-expanded','true'); this.launcher.hidden = true;
      return this.bootstrap();
    }
    close(focus = true) {
      this.invalidate(); this.view.replaceChildren(); this.messages.clear(); this.profile = null; this.csrf = null;
      this.log = null; this.form = null; this.content = null; this.safety = null; this.say();
      if (this.panel.open) this.panel.close();
      this.launcher.setAttribute('aria-expanded','false'); this.launcher.hidden = Boolean(document.querySelector(overlaySelector));
      if (focus && !this.launcher.hidden) this.launcher.focus({preventScroll:true});
    }
    async request(path, body, csrf) {
      const sequence = this.sequence;
      const controller = new AbortController(); this.requests.add(controller);
      const timer = setTimeout(() => controller.abort(),15000);
      try {
        const options = {credentials:'same-origin',cache:'no-store',signal:controller.signal,headers:{Accept:'application/json','X-Chat-Customer':this.owner || 'guest'}};
        if (body !== undefined) { options.method = 'POST'; options.headers['Content-Type'] = 'application/json'; options.headers['X-CSRF-Token'] = csrf; options.body = JSON.stringify(body); }
        const response = await fetch('/apps/jacobs-community/chat/' + path,options); const data = await response.json();
        if (!response.ok) {
          const error = new Error(data?.error?.message || this.word('failed')); error.code = data?.error?.code; error.status = response.status;
          if ((error.status === 401 || error.code === 'chat_identity_changed') && this.current(sequence)) this.resetIdentity();
          throw error;
        }
        return data;
      } finally { clearTimeout(timer); this.requests.delete(controller); }
    }
    async session(sequence) {
      const data = await this.request('session'); if (!this.current(sequence)) return null;
      if (!data.authenticated) { this.resetIdentity(); return null; }
      if (!data.csrfToken) throw new Error(this.word('failed'));
      if (this.profile && data.profile?.id !== this.profile.id) { this.resetIdentity(); return null; }
      this.csrf = data.csrfToken; return data;
    }
    async write(path, body, sequence) {
      if (!this.current(sequence)) throw new Error(this.word('signedout'));
      if (!this.csrf && !await this.session(sequence)) throw new Error(this.word('signedout'));
      try { return await this.request(path,body,this.csrf); }
      catch (error) {
        if (error.code !== 'invalid_csrf' || !this.current(sequence)) throw error;
        if (!await this.session(sequence) || !this.current(sequence)) throw error;
        return this.request(path,body,this.csrf);
      }
    }
    handleError(error, sequence, retry) {
      if (!this.current(sequence)) return;
      if (['chat_join_required','chat_access_unavailable','chat_verification_required'].includes(error.code)) {
        this.invalidate(); this.clearPrivate(); this.say(error.message);
        const box = node('div','jl-chat-welcome'); box.append(node('p','',this.word('access_unavailable')));
        const button = action(this.word('retry'),'retry',true); button.addEventListener('click',() => this.bootstrap(),{once:true}); box.append(button); this.view.replaceChildren(box); return;
      }
      if (error.status === 404 && this.target?.conversationId) {
        const key = this.targetKey(); this.invalidate(); this.drafts.delete(key); this.pending.delete(key); this.messages.clear(); this.target = null; this.form = null; this.log = null;
        this.content.replaceChildren(node('p','jl-chat-empty',this.word('conversation_unavailable'))); this.say(error.message); return;
      }
      this.say(error.name === 'AbortError' ? this.word('failed') : error.message || this.word('failed'),retry);
    }
    async bootstrap() {
      this.invalidate(); const sequence = this.sequence;
      this.view.replaceChildren(); this.messages.clear(); this.log = null; this.form = null;
      if (!this.identityCurrent()) { this.resetIdentity(); return; }
      if (!this.owner) { this.say(); this.guest(); return; }
      this.say(this.word('loading'));
      try {
        const data = await this.session(sequence); if (!data) return;
        this.profile = data.profile; this.say();
        if (!this.profile) { this.joinForm(); return; }
        this.shell(); await this.select('lounge');
      } catch (error) { this.handleError(error,sequence,() => this.bootstrap()); }
    }
    guest() {
      const welcome = node('div','jl-chat-welcome');
      welcome.append(node('div','jl-chat-symbol','✦'),node('h3','',this.word('welcome')),node('p','',this.word('description')));
      const options = node('div','jl-chat-options');
      for (const key of ['shared','private']) { const item = node('div'); item.append(node('strong','',this.word(key)),node('span','',this.word(key + '_detail'))); options.append(item); }
      const signin = node('a','jl-chat-button',this.word('signin')); signin.href = this.dataset.loginUrl || '/account/login';
      welcome.append(options,signin); this.view.replaceChildren(welcome);
    }
    joinForm() {
      const form = node('form','jl-chat-welcome'); form.dataset.chatForm = 'join';
      form.append(node('h3','',this.word('join_title')),node('p','',this.word('join_detail')));
      const label = node('label','jl-chat-field',this.word('display_name'));
      const input = node('input'); input.name = 'displayName'; input.required = true; input.minLength = 2; input.maxLength = 32; input.autocomplete = 'nickname'; label.append(input);
      const consent = node('label','jl-chat-consent'); const checkbox = node('input'); checkbox.type = 'checkbox'; checkbox.name = 'consent'; checkbox.required = true;
      consent.append(checkbox,node('span','',this.word('join_consent')));
      const submit = action(this.word('join'),'submit'); submit.type = 'submit';
      form.append(label,node('p','jl-chat-hint',this.word('name_hint')),consent,submit); this.view.replaceChildren(form);
    }
    shell() {
      const app = node('div','jl-chat-app'); const tabs = node('nav','jl-chat-tabs'); tabs.setAttribute('aria-label',this.word('navigation'));
      for (const key of ['lounge','inbox','people','blocked']) { const button = action(this.word(key === 'inbox' ? 'inbox_tab' : key),'tab',true); button.dataset.tab = key; tabs.append(button); }
      this.tabs = tabs; this.content = node('section','jl-chat-content'); app.append(tabs,this.content); this.view.replaceChildren(app);
    }
    targetKey(target = this.target) { return target?.conversationId ? 'private:' + target.conversationId : 'room:community'; }
    async select(mode, target) {
      if (!this.profile || !this.panel.open || this.signingOut) return;
      this.invalidate(); const sequence = this.sequence; this.mode = mode; this.target = target || (mode === 'lounge' ? {roomId:'community',name:this.word('lounge')} : null);
      this.messages.clear(); this.cursor = null; this.previous = null; this.hasOlder = false; this.form = null; this.log = null; this.safety = null; this.content.replaceChildren();
      for (const button of this.tabs.querySelectorAll('button')) button.setAttribute('aria-current',button.dataset.tab === (mode === 'conversation' ? 'inbox' : mode) ? 'page' : 'false');
      this.say();
      if (this.target) {
        this.thread();
        try { await this.loadMessages(sequence); } catch (error) { this.handleError(error,sequence,() => this.select(mode,target)); }
      } else if (mode === 'people') {
        const form = node('form','jl-chat-search'); form.dataset.chatForm = 'search';
        const label = node('label','jl-chat-field',this.word('find_people')); const input = node('input'); input.name = 'q'; input.type = 'search'; input.maxLength = 32; input.placeholder = this.word('search_placeholder'); label.append(input);
        const submit = action(this.word('search'),'submit',true); submit.type = 'submit'; form.append(label,submit);
        this.results = node('div','jl-chat-list'); this.content.append(form,node('p','jl-chat-hint',this.word('people_hint')),this.results); await this.searchPeople('',sequence);
      } else {
        this.content.append(node('h3','jl-chat-section-title',this.word(mode))); this.results = node('div','jl-chat-list'); this.content.append(this.results);
        try { await this.loadList(sequence); } catch (error) { this.handleError(error,sequence,() => this.select(mode)); }
      }
      this.schedule(sequence);
    }
    thread() {
      const head = node('div','jl-chat-thread-head'); head.append(node('h3','',this.target.name),node('p','',this.word(this.target.conversationId ? 'private_visibility' : 'public_visibility')));
      if (this.target.conversationId) { const block = action(this.word('block'),'block',true); block.dataset.profileId = this.target.participant.id; block.dataset.name = this.target.name; head.append(block); }
      this.safety = node('div','jl-chat-safety'); this.older = action(this.word('older'),'older',true); this.older.hidden = true;
      this.log = node('div','jl-chat-messages'); this.log.setAttribute('role','log'); this.log.setAttribute('aria-label',this.target.name); this.log.setAttribute('aria-live','off'); this.log.tabIndex = 0; this.log.append(node('p','jl-chat-empty',this.word('loading')));
      const form = node('form','jl-chat-compose'); form.dataset.chatForm = 'message';
      const label = node('label','jl-chat-field',this.word('message')); const input = node('textarea'); input.name = 'text'; input.required = true; input.maxLength = 1200; input.rows = 2; input.placeholder = this.word('message_placeholder'); label.append(input);
      const controls = node('div','jl-chat-compose-actions'); controls.append(node('span','jl-chat-hint',this.word('message_hint')));
      const submit = action(this.word('send'),'submit'); submit.type = 'submit'; controls.append(submit); form.append(label,controls);
      this.form = form; this.sendButton = submit; this.content.append(head,this.safety,this.older,this.log,form); this.updateCompose();
    }
    updateCompose() {
      if (!this.form || !this.target) return;
      const pending = this.pending.get(this.targetKey()); const input = this.form.elements.text;
      input.value = pending?.body.text ?? this.drafts.get(this.targetKey()) ?? ''; input.readOnly = Boolean(pending); this.sendButton.disabled = Boolean(pending?.sending);
      this.sendButton.textContent = this.word(pending ? pending.sending ? 'sending' : 'retry_send' : 'send');
    }
    messagePath(extra = {}) {
      const parameters = new URLSearchParams(this.target.conversationId ? {conversationId:this.target.conversationId} : {roomId:'community'});
      for (const [key,value] of Object.entries(extra)) if (value) parameters.set(key,value);
      return 'messages?' + parameters.toString();
    }
    async loadMessages(sequence, before = false) {
      const data = await this.request(this.messagePath(before ? {before:this.previous} : this.cursor ? {after:this.cursor} : {})); if (!this.current(sequence)) return;
      if (before || !this.cursor) { this.previous = data.previousCursor; this.hasOlder = Boolean(data.hasMoreBefore); }
      if (!before && data.nextCursor) this.cursor = data.nextCursor;
      this.mergeMessages(data.messages || [],before); this.older.hidden = !this.hasOlder;
      const pending = this.pending.get(this.targetKey()); this.say(pending ? this.word(pending.sending ? 'sending' : 'uncertain') : '');
      if (!before && data.hasMore) await this.loadMessages(sequence);
    }
    mergeMessages(items, before = false) {
      if (!this.log) return;
      const oldHeight = this.log.scrollHeight; const oldTop = this.log.scrollTop; const atBottom = oldHeight - oldTop - this.log.clientHeight < 80;
      let added = 0;
      for (const item of items) if (item?.id && item.author?.id && typeof item.text === 'string' && !this.messages.has(item.id)) { this.messages.set(item.id,item); added++; }
      if (!added && this.messages.size) return;
      const sorted = [...this.messages.values()].sort((a,b) => String(a.createdAt).localeCompare(String(b.createdAt)) || String(a.id).localeCompare(String(b.id)));
      this.log.replaceChildren();
      for (const message of sorted) {
        const own = message.author.id === this.profile.id; const article = node('article','jl-chat-message' + (own ? ' jl-chat-message--own' : ''));
        const meta = node('div','jl-chat-message-meta'); meta.append(node('strong','',message.author.displayName)); const time = node('time'); const date = new Date(message.createdAt);
        if (!Number.isNaN(date.getTime())) { time.dateTime = date.toISOString(); time.textContent = date.toLocaleString(undefined,{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}); }
        meta.append(time); article.append(meta,node('p','jl-chat-message-text',message.text));
        if (!own) {
          const details = node('details','jl-chat-message-actions'); details.append(node('summary','',this.word('message_options')));
          const report = action(this.word('report'),'report',true); report.dataset.messageId = message.id;
          const block = action(this.word('block'),'block',true); block.dataset.profileId = message.author.id; block.dataset.name = message.author.displayName; details.append(report,block); article.append(details);
        }
        this.log.append(article);
      }
      if (!sorted.length) this.log.append(node('p','jl-chat-empty',this.word('empty_messages')));
      if (before) this.log.scrollTop = oldTop + this.log.scrollHeight - oldHeight; else if (atBottom || !oldHeight) this.log.scrollTop = this.log.scrollHeight; else this.log.scrollTop = oldTop;
    }
    async loadList(sequence) {
      const data = await this.request(this.mode === 'blocked' ? 'blocks' : 'conversations'); if (!this.current(sequence)) return;
      this.results.replaceChildren(); const items = this.mode === 'blocked' ? data.members || [] : data.conversations || [];
      for (const item of items) {
        const member = item.participant || item; const row = node('div','jl-chat-person'); row.append(node('strong','',member.displayName));
        const button = action(this.word(this.mode === 'blocked' ? 'unblock' : 'open_conversation'),this.mode === 'blocked' ? 'unblock' : 'conversation',true);
        button.dataset.profileId = member.id; button.dataset.name = member.displayName; if (item.participant) button.dataset.conversationId = item.id; row.append(button); this.results.append(row);
      }
      if (!items.length) this.results.append(node('p','jl-chat-empty',this.word(this.mode === 'blocked' ? 'empty_blocked' : 'empty_inbox')));
    }
    async searchPeople(query, sequence = this.sequence) {
      const search = ++this.searchSequence;
      try {
        const data = await this.request('members?q=' + encodeURIComponent(query)); if (!this.current(sequence) || search !== this.searchSequence) return;
        this.results.replaceChildren();
        for (const member of data.members || []) { const row = node('div','jl-chat-person'); row.append(node('strong','',member.displayName)); const button = action(this.word('start_private'),'start_private',true); button.dataset.profileId = member.id; button.dataset.name = member.displayName; row.append(button); this.results.append(row); }
        if (!data.members?.length) this.results.append(node('p','jl-chat-empty',this.word('empty_people'))); this.say();
      } catch (error) { if (search === this.searchSequence) this.handleError(error,sequence); }
    }
    schedule(sequence) {
      clearTimeout(this.timer);
      if (!this.current(sequence) || !this.profile || this.mode === 'people' || this.mode === 'blocked') return;
      this.timer = setTimeout(() => this.poll(sequence),8000);
    }
    async poll(sequence) {
      if (!this.current(sequence) || this.polling) return;
      this.polling = true;
      try { if (this.target) await this.loadMessages(sequence); else await this.loadList(sequence); }
      catch (error) { this.handleError(error,sequence); }
      finally { if (sequence === this.sequence) this.polling = false; this.schedule(sequence); }
    }
    async send() {
      if (!this.form || !this.target || !this.profile) return;
      const key = this.targetKey(); let pending = this.pending.get(key); if (pending?.sending) return;
      if (!pending) {
        const text = this.form.elements.text.value.trim(); if (!text || text.length > 1200) { this.form.reportValidity(); return; }
        const target = this.target.conversationId ? {conversationId:this.target.conversationId} : {roomId:'community'};
        pending = {body:Object.freeze({...target,text,clientMessageId:crypto.randomUUID()}),sending:false}; this.pending.set(key,pending);
      }
      pending.sending = true; this.updateCompose(); const sequence = this.sequence; this.say(this.word('sending'));
      try {
        const result = await this.write('messages',pending.body,sequence);
        if (this.pending.get(key) !== pending || !this.identityCurrent()) return;
        this.pending.delete(key); this.drafts.delete(key);
        if (this.current(sequence)) { this.mergeMessages([result.message]); this.updateCompose(); this.say(this.word('sent')); }
      } catch (error) {
        if (this.pending.get(key) === pending) pending.sending = false;
        if (this.current(sequence)) {
          if (error.status >= 400 && error.status < 500 && ![408,409].includes(error.status)) { this.pending.delete(key); this.drafts.set(key,pending.body.text); }
          this.updateCompose(); this.handleError(error,sequence); if (this.pending.has(key) && this.current(sequence)) this.say(this.word('uncertain'));
        }
      } finally { if (this.pending.get(key) === pending) pending.sending = false; }
    }
    safetyForm(kind, button) {
      if (!this.safety) return;
      const form = node('form','jl-chat-safety-form'); form.dataset.chatForm = kind; form.dataset.messageId = button.dataset.messageId || ''; form.dataset.profileId = button.dataset.profileId || '';
      form.append(node('strong','',this.word(kind === 'report' ? 'report_title' : 'block_title')),node('p','',this.word(kind === 'report' ? 'report_detail' : 'block_detail')));
      if (kind === 'report') { const label = node('label','jl-chat-field',this.word('reason')); const input = node('textarea'); input.name = 'reason'; input.required = true; input.maxLength = 500; input.rows = 2; label.append(input); form.append(label); }
      const submit = action(this.word(kind === 'report' ? 'save_report' : 'block'),'submit'); submit.type = 'submit'; form.append(submit,action(this.word('cancel'),'cancel_safety',true));
      this.safety.replaceChildren(form); form.querySelector('textarea,button')?.focus({preventScroll:true});
    }
    async submit(event) {
      const form = event.target; if (!form?.dataset.chatForm || !this.contains(form)) return;
      event.preventDefault(); if (!form.reportValidity() || !this.current(this.sequence)) return;
      const kind = form.dataset.chatForm;
      if (kind === 'message') return this.send(); if (kind === 'search') return this.searchPeople(form.elements.q.value.trim());
      if (form.dataset.busy === 'true') return;
      form.dataset.busy = 'true'; const sequence = this.sequence; const submit = form.querySelector('button[type="submit"]'); if (submit) submit.disabled = true;
      try {
        if (kind === 'join') { await this.write('join',{displayName:form.elements.displayName.value.trim()},sequence); if (this.current(sequence)) await this.bootstrap(); }
        else if (kind === 'report') { await this.write('report',{messageId:form.dataset.messageId,reason:form.elements.reason.value.trim()},sequence); if (this.current(sequence)) { this.safety.replaceChildren(); this.say(this.word('reported')); } }
        else if (kind === 'block') { await this.write('block',{profileId:form.dataset.profileId},sequence); if (this.current(sequence)) { await this.select('lounge'); this.say(this.word('blocked_notice')); } }
      } catch (error) { this.handleError(error,sequence); }
      finally { form.dataset.busy = 'false'; if (submit) submit.disabled = false; }
    }
    async click(event) {
      const button = event.target.closest?.('[data-chat-action]'); if (!button || !this.contains(button) || button.disabled) return;
      const name = button.dataset.chatAction; if (name === 'submit' || name === 'retry') return;
      event.preventDefault(); if (!this.current(this.sequence)) return;
      if (name === 'tab') return this.select(button.dataset.tab);
      if (name === 'conversation') return this.select('conversation',{conversationId:button.dataset.conversationId,name:button.dataset.name,participant:{id:button.dataset.profileId,displayName:button.dataset.name}});
      if (name === 'report' || name === 'block') return this.safetyForm(name,button);
      if (name === 'cancel_safety') { this.safety?.replaceChildren(); return; }
      const sequence = this.sequence; button.disabled = true;
      try {
        if (name === 'older') await this.loadMessages(sequence,true);
        if (name === 'start_private') { const result = await this.write('conversations',{recipientId:button.dataset.profileId},sequence); if (this.current(sequence)) await this.select('conversation',{conversationId:result.conversation.id,name:result.conversation.participant.displayName,participant:result.conversation.participant}); }
        if (name === 'unblock') { await this.write('unblock',{profileId:button.dataset.profileId},sequence); if (this.current(sequence)) { await this.loadList(sequence); this.say(this.word('unblocked_notice')); } }
      } catch (error) { this.handleError(error,sequence); }
      finally { button.disabled = false; }
    }
  }
  if (!customElements.get('jl-community-chat')) customElements.define('jl-community-chat',CommunityChat);
})();
